import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Badge,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SchoolIcon from "@mui/icons-material/School";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import MicIcon from "@mui/icons-material/Mic";
import AssessmentIcon from "@mui/icons-material/Assessment";
import GroupsIcon from "@mui/icons-material/Groups";
import FolderIcon from "@mui/icons-material/Folder";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import FlagIcon from "@mui/icons-material/Flag";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { UserRole } from "@/api/generated.schemas";
import { useGetPendingApprovals } from "@/api/generated";

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  requiredRoles?: UserRole[];
}

const mainNavItems: NavItem[] = [
  { label: "Dashboard", path: "/dashboard", icon: <DashboardIcon /> },
  { label: "Learning Journey", path: "/pathways", icon: <SchoolIcon /> },
  { label: "Goals", path: "/goals", icon: <FlagIcon /> },
  { label: "AI Coach", path: "/ai-coach", icon: <SmartToyIcon /> },
  { label: "Calls", path: "/transcripts", icon: <MicIcon /> },
  { label: "Call Performance", path: "/call-performance", icon: <TrendingUpIcon /> },
  { label: "Community", path: "/community", icon: <GroupsIcon /> },
  { label: "My Knowledgebase", path: "/my-kb", icon: <PlayCircleOutlineIcon /> },
  { label: "Resources", path: "/resources", icon: <FolderIcon /> },
];

const managerNavItems: NavItem[] = [
  {
    label: "Reports",
    path: "/reports",
    icon: <AssessmentIcon />,
    requiredRoles: [UserRole.manager, UserRole.director, UserRole.administrator],
  },
  {
    label: "Pending Approvals",
    path: "/goals/approvals",
    icon: <FactCheckIcon />,
    requiredRoles: [UserRole.manager, UserRole.director, UserRole.administrator],
  },
];

const secondaryNavItems: NavItem[] = [
  {
    label: "Team Management",
    path: "/admin/team-management",
    icon: <PeopleIcon />,
    requiredRoles: [UserRole.administrator],
  },
  {
    label: "Admin Settings",
    path: "/admin/settings",
    icon: <SettingsIcon />,
    requiredRoles: [UserRole.administrator],
  },
];

const footerNavItems: NavItem[] = [{ label: "About", path: "/about", icon: <InfoOutlinedIcon /> }];

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 64;

export function SidebarNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole } = useAuth();
  const { collapsed, toggleCollapsed } = useSidebar();

  // Fetch pending approvals count for managers+
  const isManager = hasRole([UserRole.manager, UserRole.director, UserRole.administrator]);
  const { data: pendingApprovals } = useGetPendingApprovals({
    query: {
      enabled: isManager,
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      refetchOnWindowFocus: true, // Refetch when tab gains focus
    },
  });
  const pendingCount = pendingApprovals
    ? (pendingApprovals.goal_creation_count ?? 0) +
      (pendingApprovals.milestone_creation_count ?? 0) +
      pendingApprovals.creation_count +
      pendingApprovals.completion_count
    : 0;

  const filterByRole = (items: NavItem[]) =>
    items.filter((item) => !item.requiredRoles || hasRole(item.requiredRoles));

  const visibleMainItems = filterByRole(mainNavItems);
  const visibleManagerItems = filterByRole(managerNavItems);
  const visibleSecondaryItems = filterByRole(secondaryNavItems);
  const visibleFooterItems = filterByRole(footerNavItems);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path);
    const showBadge = item.path === "/goals/approvals" && pendingCount > 0;

    const icon = showBadge ? (
      <Badge badgeContent={pendingCount} color="error" max={99}>
        {item.icon}
      </Badge>
    ) : (
      item.icon
    );

    const button = (
      <ListItemButton
        key={item.path}
        selected={active}
        onClick={() => handleNavigation(item.path)}
        aria-current={active ? "page" : undefined}
        sx={{
          minHeight: 48,
          justifyContent: collapsed ? "center" : "flex-start",
          px: collapsed ? 2 : 2.5,
          "&.Mui-selected": {
            bgcolor: "rgba(227, 24, 55, 0.08)",
            borderLeft: "3px solid",
            borderColor: "primary.main",
            "& .MuiListItemIcon-root": {
              color: "primary.main",
            },
            "& .MuiListItemText-primary": {
              fontWeight: 600,
              color: "primary.main",
            },
          },
          "&:hover": {
            bgcolor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 40,
            justifyContent: "center",
          }}
        >
          {icon}
        </ListItemIcon>
        {!collapsed && <ListItemText primary={item.label} />}
      </ListItemButton>
    );

    if (collapsed) {
      return (
        <Tooltip key={item.path} title={item.label} placement="right" arrow>
          {button}
        </Tooltip>
      );
    }

    return button;
  };

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{
        position: "relative",
        width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        flexShrink: 0,
        bgcolor: "background.paper",
        borderRight: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        transition: "width 200ms ease-in-out",
        overflow: "hidden",
      }}
    >
      {/* Collapse toggle button - floating on edge */}
      <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right" arrow>
        <IconButton
          onClick={toggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          size="small"
          sx={{
            position: "absolute",
            right: -12,
            top: 12,
            zIndex: 1,
            width: 24,
            height: 24,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            "&:hover": {
              bgcolor: "grey.100",
            },
          }}
        >
          {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {/* Main navigation */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden", py: 1 }}>
        <List disablePadding>{visibleMainItems.map(renderNavItem)}</List>

        {visibleManagerItems.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <List disablePadding>{visibleManagerItems.map(renderNavItem)}</List>
          </>
        )}

        <Divider sx={{ my: 1 }} />

        <List disablePadding>{visibleSecondaryItems.map(renderNavItem)}</List>
      </Box>

      {/* Footer navigation */}
      <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
        <List disablePadding>{visibleFooterItems.map(renderNavItem)}</List>
      </Box>
    </Box>
  );
}
